import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;
  const isHomePage = location.pathname === '/';

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/particuliers', label: t('nav.particuliers') },
    { path: '/professionnels', label: t('nav.professionnels') },
    { path: '/contact', label: t('nav.contact') },
  ];

  return (
    <>
      {/* Top Bar */}
      <div className={`hidden md:block transition-all duration-300 ${isHomePage && !isScrolled ? 'bg-transparent' : 'bg-[#1A1A1A]'}`}>
        <div className="container-luxury">
          <div className="flex justify-end items-center py-2 gap-6">
            <a href="tel:+21696050699" className="flex items-center gap-2 text-sm text-white/70 hover:text-[#C9A227] transition-colors" data-testid="phone-link">
              <Phone className="w-4 h-4" />
              <span>+216 96 050 699</span>
            </a>
            <a href="mailto:commercial@excelsium.online" className="flex items-center gap-2 text-sm text-white/70 hover:text-[#C9A227] transition-colors" data-testid="email-link">
              <Mail className="w-4 h-4" />
              <span>commercial@excelsium.online</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav 
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled || !isHomePage
            ? 'bg-[#FDFCF8] shadow-lg' 
            : 'bg-[#FDFCF8]'
        }`}
        data-testid="main-navigation"
      >
        <div className="container-luxury">
          <div className="flex items-center justify-between py-4">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_visa-business/artifacts/6dls0pvg_logo%20full%20excelsium.jpg" 
                alt="Excelsium Conseils" 
                className="h-24 md:h-28 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link ${isActive(link.path) ? 'active' : ''}`}
                  data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Right side: Language Switcher */}
            <div className="hidden md:flex items-center gap-4">
              <LanguageSwitcher />
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-3">
              <LanguageSwitcher />
              <button 
                className="p-2 text-[#1A1A1A]"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div 
        className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}
        data-testid="mobile-menu"
      >
        <button 
          className="absolute top-6 right-6 p-2"
          onClick={() => setIsMobileMenuOpen(false)}
          data-testid="mobile-menu-close"
        >
          <X className="w-6 h-6 text-[#1A1A1A]" />
        </button>

        <Link to="/" className="mb-8" onClick={() => setIsMobileMenuOpen(false)}>
          <img 
            src="https://customer-assets.emergentagent.com/job_visa-business/artifacts/6dls0pvg_logo%20full%20excelsium.jpg" 
            alt="Excelsium Conseils" 
            className="h-32 w-auto"
          />
        </Link>

        {navLinks.map((link) => (
          <Link
            key={link.path}
            to={link.path}
            className={`text-2xl font-serif ${isActive(link.path) ? 'text-[#C9A227]' : 'text-[#1A1A1A]'} hover:text-[#C9A227] transition-colors`}
            onClick={() => setIsMobileMenuOpen(false)}
            data-testid={`mobile-nav-${link.path.replace('/', '') || 'home'}`}
          >
            {link.label}
          </Link>
        ))}

        <div className="mt-8 flex flex-col items-center gap-4 text-sm text-gray-600">
          <a href="tel:+21696050699" className="flex items-center gap-2 hover:text-[#C9A227]">
            <Phone className="w-4 h-4" />
            +216 96 050 699
          </a>
          <a href="mailto:commercial@excelsium.online" className="flex items-center gap-2 hover:text-[#C9A227]">
            <Mail className="w-4 h-4" />
            commercial@excelsium.online
          </a>
        </div>
      </div>
    </>
  );
};

export default Navigation;
